import { computed, shallowRef } from "vue";

import { empire } from "@/empire";
import { WrongAuthorityContextError } from "@/lib/errors";

export type StartupStatus = "idle" | "loading" | "ready" | "error";

const status = shallowRef<StartupStatus>("idle");
const startupError = shallowRef<unknown>(null);
let startupPromise: Promise<void> | null = null;

export interface AuthorityAuthStartupClient {
  isAuthenticated: boolean;
  session: { application_id?: string | null } | null;
  completeRedirect(): Promise<boolean>;
  initialize(): Promise<void>;
  logout(): Promise<void>;
}

export async function initializeAuthoritySession(auth: AuthorityAuthStartupClient): Promise<void> {
  const completed = await auth.completeRedirect();
  if (!completed) await auth.initialize();

  if (auth.isAuthenticated && auth.session?.application_id !== null) {
    await auth.logout();
    throw new WrongAuthorityContextError();
  }
}

async function performStartup(): Promise<void> {
  status.value = "loading";
  startupError.value = null;

  try {
    await initializeAuthoritySession(empire.auth);
    status.value = "ready";
  } catch (error) {
    startupError.value = error;
    status.value = "error";
    throw error;
  }
}

export function ensureAuthStartup(): Promise<void> {
  if (status.value === "ready") return Promise.resolve();
  if (startupPromise) return startupPromise;

  startupPromise = performStartup().finally(() => {
    startupPromise = null;
  });
  return startupPromise;
}

export async function retryAuthStartup(): Promise<void> {
  status.value = "idle";
  await ensureAuthStartup();
}

export const authStartup = {
  status: computed(() => status.value),
  error: computed(() => startupError.value),
};
